import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
} from '@angular/core';
import {
  AvatarComponent,
  BadgeComponent,
  BadgeModule,
  BreadcrumbRouterComponent,
  ButtonDirective,
  ColorModeService,
  ContainerComponent,
  DropdownComponent,
  DropdownDividerDirective,
  DropdownHeaderDirective,
  DropdownItemDirective,
  DropdownMenuDirective,
  DropdownToggleDirective,
  HeaderComponent,
  HeaderNavComponent,
  HeaderTogglerDirective,
  NavItemComponent,
  NavLinkDirective,
  ProgressBarDirective,
  ProgressComponent,
  SidebarToggleDirective,
  TextColorDirective,
  ThemeDirective,
  ToastBodyComponent,
  ToastComponent,
  ToasterComponent,
  ToastHeaderComponent,
  TooltipModule,
} from '@coreui/angular-pro';
import { CommonModule, NgStyle, NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { IconDirective } from '@coreui/icons-angular';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { delay, filter, map, tap } from 'rxjs/operators';
import { UserProfileService } from '../../../services/user-profile.service';
import { NotificationService } from '../../../services/notification.service';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { ContractsService } from '../../../services/contracts.service';
import { OrdersService } from '../../../services/orders.service';

@Component({
  selector: 'app-default-header',
  templateUrl: './default-header.component.html',
  styleUrls: ['./default-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: true,
  imports: [
    AvatarComponent,
    BadgeComponent,
    BadgeModule,
    BreadcrumbRouterComponent,
    ButtonDirective,
    ContainerComponent,
    CommonModule,
    DropdownMenuDirective,
    DropdownHeaderDirective,
    DropdownItemDirective,
    DropdownDividerDirective,
    DropdownComponent,
    DropdownToggleDirective,
    FormsModule,
    HeaderNavComponent,
    HeaderTogglerDirective,
    IconDirective,
    NavItemComponent,
    NavLinkDirective,
    NgTemplateOutlet,
    NgStyle,
    ProgressBarDirective,
    ProgressComponent,
    RouterLink,
    RouterLinkActive,
    SidebarToggleDirective,
    TextColorDirective,
    ThemeDirective,
    ToastComponent,
    ToastHeaderComponent,
    ToastBodyComponent,
    ToasterComponent,
    TooltipModule,
    TranslocoDirective,
  ],
})
export class DefaultHeaderComponent
  extends HeaderComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() sidebarId: string = 'sidebar1';
  @ViewChild('notificationDropdown', { read: ElementRef }) notificationDropdown: ElementRef | undefined;
  readonly #activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  readonly #colorModeService = inject(ColorModeService);
  readonly colorMode = this.#colorModeService.colorMode;
  readonly #destroyRef: DestroyRef = inject(DestroyRef);

  readonly colorModes = [
    { name: 'light', text: 'Light', icon: 'cilSun' },
    { name: 'dark', text: 'Dark', icon: 'cilMoon' },
    { name: 'auto', text: 'Auto', icon: 'cilContrast' },
  ];
  currentMode: string = 'light';

  readonly languages = [
    { name: 'ENG', text: 'English', code: 'en' },
    { name: 'TAG', text: 'Tagalog', code: 'tl' },
    { name: 'ILC', text: 'Ilocano', code: 'il' },
    { name: 'BIS', text: 'Bisaya', code: 'bs' },
    { name: 'KAP', text: 'Kapampanga', code: 'kp' },
  ];

  readonly icons = computed(() => {
    const currentMode = this.colorMode();
    const themeMode = this.#colorModeService.getDatasetTheme() || 'light';
    if (themeMode === 'auto') {
      this.currentMode = 'light';
    } else {
      this.currentMode = themeMode;
    }
    console.log(
      'currentMode: ',
      themeMode,
      this.#colorModeService.getDatasetTheme()
    );
    return (
      this.colorModes.find((mode) => mode.name === currentMode)?.icon ??
      'cilSun'
    );
  });

  language: string = 'ENG';
  numUpdates: number = 5;
  numPosts: number = 0;
  numPayments: number = 0;
  updateVisibleSecs: number = 5;
  autoHideUpdates: boolean = true;
  notificationVisibility: Map<string, boolean> = new Map();
  notificationAutoHide: Map<string, boolean> = new Map();
  
  // New properties for dropdown panel
  showFilter: boolean = false;
  notificationFilter: string = 'all';
  maxNotificationsShown: number = 10;
  hoveredNotification: string | null = null;
  showNotificationDropdown: boolean = false;

  // tooltips
  showMarketTracker: boolean = false;
  showMarketPlace: boolean = false;
  showMyPosts: boolean = false;
  showContracts: boolean = false;

  public newTasks = [
    { id: 0, title: 'Upgrade NPM', value: 0, color: 'info' },
    { id: 1, title: 'ReactJS Version', value: 25, color: 'danger' },
    { id: 2, title: 'VueJS Version', value: 50, color: 'warning' },
    { id: 3, title: 'Add new layouts', value: 75, color: 'info' },
    { id: 4, title: 'Angular Version', value: 100, color: 'success' },
  ];

  constructor(
    public userService: UserProfileService,
    public notificationService: NotificationService,
    private translocoService: TranslocoService,
    public contractsService: ContractsService,
    public orderService: OrdersService,
    public changeDetectorRef: ChangeDetectorRef,
    private router: Router
  ) {
    super();

    this.#colorModeService.localStorageItemName.set(
      'coreui-free-angular-admin-template-theme-default'
    );
    this.#colorModeService.eventName.set('ColorSchemeChange');

    this.#activatedRoute.queryParams
      .pipe(
        delay(1),
        map((params) => <string>params['theme']?.match(/^[A-Za-z0-9\s]+/)?.[0]),
        filter((theme) => ['dark', 'light', 'auto'].includes(theme)),
        tap((theme) => {
          this.colorMode.set(theme);
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.randomToolTip();
    
    // Initialize visibility for existing notifications
    if (this.userService.loggedIn) {
      this.loadNotificationVisibilityState();
      this.initializeNotificationVisibility();
    }
  }

  ngAfterViewInit(): void {
    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy(): void {
    // Clean up event listener
    document.removeEventListener('click', this.clickOutsideHandler);
  }

  private randomToolTip() {
    let num = Math.floor(Math.random() * 100) % 4;
    switch (num) {
      case 0:
        this.showMarketTracker = true;
        setTimeout(() => {
          this.showMarketTracker = false;
        }, 10000);
        break;
      case 1:
        this.showMarketPlace = true;
        setTimeout(() => {
          this.showMarketPlace = false;
        }, 10000);
        break;
      case 2:
        this.showMyPosts = true;
        setTimeout(() => {
          this.showMyPosts = false;
        }, 10000);
        break;
      case 3:
        this.showContracts = true;
        setTimeout(() => {
          this.showContracts = false;
        }, 10000);
        break;
    }
  }

  public languageSet(lang: any) {
    this.language = lang.name;
    this.translocoService.setActiveLang(lang.code);
  }

  toggleShowUpdates() {
    this.notificationService.updateDuration();
    // The dropdown will handle the visibility toggle
    // We just need to update durations when opened
  }

  toggleNotificationDropdown() {
    this.showNotificationDropdown = !this.showNotificationDropdown;
    if (this.showNotificationDropdown) {
      this.notificationService.updateDuration();
      
      // Add click outside listener
      setTimeout(() => {
        document.addEventListener('click', this.clickOutsideHandler);
      }, 0);
    } else {
      document.removeEventListener('click', this.clickOutsideHandler);
    }
  }

  private clickOutsideHandler = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    const dropdown = document.querySelector('.notification-dropdown-menu');
    const button = document.querySelector('.btn.btn-ghost');
    
    if (dropdown && !dropdown.contains(target) && button && !button.contains(target)) {
      this.showNotificationDropdown = false;
      document.removeEventListener('click', this.clickOutsideHandler);
      this.changeDetectorRef.detectChanges();
    }
  };


  private initializeNotificationVisibility() {
    this.notificationService.myNotifications.forEach(notification => {
      if (notification.key && !this.notificationVisibility.has(notification.key)) {
        // Only set visible if the notification still exists in the service
        // (hasn't been deleted)
        const stillExists = this.notificationService.myNotifications.some(
          n => n.key === notification.key
        );
        if (stillExists) {
          this.notificationVisibility.set(notification.key, true);
          this.notificationAutoHide.set(notification.key, true);
        }
      }
    });
    
    // Clean up visibility entries for deleted notifications
    const currentNotificationKeys = new Set(
      this.notificationService.myNotifications
        .filter(n => n.key)
        .map(n => n.key)
    );
    
    // Remove visibility entries for notifications that no longer exist
    Array.from(this.notificationVisibility.keys()).forEach(key => {
      if (!currentNotificationKeys.has(key)) {
        this.notificationVisibility.delete(key);
        this.notificationAutoHide.delete(key);
      }
    });
    
    // Save state after initialization
    this.saveNotificationVisibilityState();
  }

  setColor(msgType: string) {
    switch (msgType) {
      case 'urgent':
      case 'danger':
        return 'danger';
      case 'warning':
        return 'warning';
      case 'info':
      case 'normal':
        return 'info';
      case 'success':
        return 'success';
      case 'update':
      default:
        return 'primary';
    }
  }

  private deleteInProgress = new Set<string>();

  deleteNotification(key: string | undefined) {
    if (key !== undefined && !this.deleteInProgress.has(key)) {
      // Prevent duplicate deletions
      this.deleteInProgress.add(key);
      
      // Immediately hide the notification in UI
      this.notificationVisibility.delete(key);
      this.notificationAutoHide.delete(key);
      
      // Delete from Firebase
      this.notificationService.deleteNotification(
        this.userService.userProfile.userID,
        key
      );
      
      // Save the updated state
      this.saveNotificationVisibilityState();
      // Trigger change detection
      this.changeDetectorRef.markForCheck();
      
      // Remove from in-progress set after a delay
      setTimeout(() => {
        this.deleteInProgress.delete(key);
      }, 500);
    }
  }

  markNotificationAsRead(key: string | undefined) {
    if (key !== undefined) {
      this.notificationService.markAsRead(
        this.userService.userProfile.userID,
        key
      );
      // Save the state after marking as read
      this.saveNotificationVisibilityState();
    }
  }
  
  hideUpdates() {
    // Reset filter when closing
    this.notificationFilter = 'all';
    this.showFilter = false;
    this.showNotificationDropdown = false;
    document.removeEventListener('click', this.clickOutsideHandler);
  }

  // Helper methods for template
  isNotificationVisible(key: string | undefined): boolean {
    if (!key) return false;
    return this.notificationVisibility.get(key) ?? true;
  }

  trackByNotificationKey(index: number, notification: any): string {
    return notification.key || index.toString();
  }

  onNotificationVisibilityChange(visible: boolean, key: string | undefined) {
    if (!visible && key) {
      // When the toast is closed using the X button, just hide it
      // Don't delete it from Firebase
      this.notificationVisibility.set(key, false);
      this.saveNotificationVisibilityState();
    }
  }

  // New methods for dropdown panel
  toggleFilter() {
    this.showFilter = !this.showFilter;
  }

  applyFilter() {
    // Filter will be applied in getFilteredNotifications()
    this.changeDetectorRef.markForCheck();
  }

  getFilteredNotifications() {
    let filtered = this.notificationService.myNotifications;
    
    switch(this.notificationFilter) {
      case 'unread':
        filtered = filtered.filter(n => !n.isRead);
        break;
      case 'trade':
        filtered = filtered.filter(n => ['normal', 'success', 'warning'].includes(n.msgType));
        break;
      case 'payment':
        filtered = filtered.filter(n => n.msgType === 'payment' || n.message.toLowerCase().includes('payment'));
        break;
      case 'system':
        filtered = filtered.filter(n => ['info', 'update'].includes(n.msgType));
        break;
    }
    
    // Sort by timestamp, newest first
    return filtered.sort((a, b) => b.updateTimestamp - a.updateTimestamp);
  }

  markAllAsRead() {
    const unreadNotifications = this.notificationService.myNotifications.filter(n => !n.isRead);
    unreadNotifications.forEach(notification => {
      if (notification.key) {
        this.notificationService.markAsRead(
          this.userService.userProfile.userID,
          notification.key
        );
      }
    });
    this.changeDetectorRef.markForCheck();
  }

  clearAllNotifications() {
    if (confirm('Are you sure you want to clear all notifications?')) {
      // Get all notification keys
      const allNotifications = [...this.notificationService.myNotifications];
      
      // Delete each notification
      allNotifications.forEach(notification => {
        if (notification.key) {
          this.deleteNotification(notification.key);
        }
      });
    }
  }

  openNotificationDropdown(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    
    // On mobile, navigate to the notification history page
    if (window.innerWidth < 768) {
      this.router.navigate(['/notifications']);
    } else {
      // On desktop, just toggle the notification dropdown
      this.toggleNotificationDropdown();
    }
  }

  shouldAutoHideNotification(key: string | undefined): boolean {
    if (!key) return this.autoHideUpdates;
    return this.notificationAutoHide.get(key) ?? this.autoHideUpdates;
  }

  // Persistence methods for notification visibility state
  private saveNotificationVisibilityState(): void {
    if (!this.userService.loggedIn || !this.userService.userProfile.userID) return;
    
    const stateKey = `notification_visibility_${this.userService.userProfile.userID}`;
    const state = {
      visibility: Array.from(this.notificationVisibility.entries()),
      autoHide: Array.from(this.notificationAutoHide.entries()),
      timestamp: Date.now()
    };
    
    try {
      localStorage.setItem(stateKey, JSON.stringify(state));
    } catch (e) {
      console.error('Failed to save notification visibility state:', e);
    }
  }

  private loadNotificationVisibilityState(): void {
    if (!this.userService.loggedIn || !this.userService.userProfile.userID) return;
    
    const stateKey = `notification_visibility_${this.userService.userProfile.userID}`;
    
    try {
      const savedState = localStorage.getItem(stateKey);
      if (savedState) {
        const state = JSON.parse(savedState);
        
        // Only load state if it's less than 24 hours old
        const dayInMs = 24 * 60 * 60 * 1000;
        if (Date.now() - state.timestamp < dayInMs) {
          this.notificationVisibility = new Map(state.visibility);
          this.notificationAutoHide = new Map(state.autoHide);
        } else {
          // Clear old state
          localStorage.removeItem(stateKey);
        }
      }
    } catch (e) {
      console.error('Failed to load notification visibility state:', e);
    }
  }

  navigateToCallToAction(link: string) {
    if (link) {
      this.router.navigate([link]);
      this.showNotificationDropdown = false; // Close dropdown after navigation
    }
  }
}
